import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'

import {
    Flex,
    Heading,
    Image,
    Text,
    useMediaQuery,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

import {styleConfigChakraKeys} from '../../constants'
import {contentfulMediaFormatter} from '../../utils'
import Link from '../link'

const Card = ({data}) => {
    const [isDesktop] = useMediaQuery('(min-width: 992px)')
    const color = data?.theme?.color ?? 'white'

    const styles = useMultiStyleConfig(styleConfigChakraKeys.homeServiceCard)

    const image = useMemo(() => {
        const assets = contentfulMediaFormatter(data?.media)
        return !isDesktop && assets?.mobile?.url
            ? assets?.mobile
            : assets?.desktop
    }, [data, isDesktop])

    if (!image) return

    const cardComponent = (
        <Flex width="100%" position="relative">
            <Image
                {...styles.img}
                src={image?.url}
                objectFit="cover"
                width="100%"
                borderRadius="50px"
                maxHeight="688px"
            />
            <Flex
                flexFlow="column"
                position="absolute"
                bottom="0"
                left="0"
                padding="40px"
            >
                {data?.title && (
                    <Heading
                        color={color}
                        fontSize="32px"
                        fontWeight="600"
                        lineHeight="40px"
                        fontFamily="Raleway"
                        textAlign={{base: 'center', md: 'start'}}
                        _hover={{
                            _hover: {
                                fontWeight: 'bold',
                                textDecoration: 'underline',
                                transition: 'all .2s ease-in'
                            }
                        }}
                    >
                        {data?.title}
                    </Heading>
                )}
                {data?.subtitle && (
                    <Text
                        color={color}
                        fontSize="26px"
                        fontWeight="400"
                        lineHeight="40px"
                        fontFamily="Raleway"
                        textAlign={{base: 'center', md: 'start'}}
                    >
                        {data?.subtitle}
                    </Text>
                )}
            </Flex>
        </Flex>
    )

    if (data?.cta)
        return (
            <Link width="100%" {..._.omit(data?.cta, ['text'])}>
                {cardComponent}
            </Link>
        )

    return cardComponent
}

Card.propTypes = {
    data: PropTypes.shape({
        title: PropTypes.string,
        subtitle: PropTypes.string,
        media: PropTypes.shape({
            fields: PropTypes.shape({
                desktop: PropTypes.shape({
                    url: PropTypes.string.isRequired
                }),
                mobile: PropTypes.shape({
                    url: PropTypes.string.isRequired
                })
            })
        }),
        cta: PropTypes.shape({
            url: PropTypes.string,
            externalUrl: PropTypes.string,
            text: PropTypes.string,
            type: PropTypes.string,
            isExternal: PropTypes.bool
        }),
        theme: PropTypes.shape({
            color: PropTypes.string,
            backgroundColor: PropTypes.string
        })
    })
}

Card.displayName = 'Card'

export default Card
